import type { Business } from '../../types';
import { formatDistance, formatRating, googleSearchHref, telHref } from '../../lib/format';
import { getOpenStatus } from '../../lib/openingHours';
import {
  LIBRARY_STATUS_LABELS,
  libraryStatusTone,
  openStatusLabel,
  websiteLabel,
  websiteTone,
} from '../lib/labels';
import { MAX_PROSPECT_SCORE, priorityRingTone, ScoreRing } from './ScoreRing';
import { IconBookmark, IconPhone, IconStar } from './Icons';

export interface RowActions {
  onSave: (business: Business) => void;
  onIgnore: (business: Business) => void;
  onUnignore: (business: Business) => void;
  onUntreat: (business: Business) => void;
  /** Arme le suivi post-appel : demande son issue au retour sur l'appli. */
  onCallStart: (business: Business) => void;
  pendingPlaceId: string | null;
}

interface ResultCardProps extends RowActions {
  business: Business;
  now: Date;
}

/**
 * Une entreprise dans la liste des résultats.
 *
 * Trois gestes seulement sont à portée du pouce, parce que ce sont les trois
 * qu'on répète des dizaines de fois d'affilée : appeler, garder, écarter. Les
 * deux premiers sont des boutons ronds à droite, à hauteur constante d'une
 * carte à l'autre — la main apprend leur position et n'a plus à viser.
 *
 * Tout le reste — reprendre une fiche déjà traitée, oublier, désigner — vit
 * dans un pied de carte qui n'apparaît que quand l'état le rend possible. Une
 * rangée de boutons toujours présents, dont la moitié serait sans objet, coûte
 * plus cher à lire qu'elle ne fait gagner.
 */
export function ResultCard(props: ResultCardProps) {
  const { business, now } = props;
  const open = getOpenStatus(business.openingHours, now);
  const pending = props.pendingPlaceId === business.placeId;
  const status = business.libraryStatus;

  const inLibrary = status === 'SAVED' || status === 'CONFIRMED';
  // « Garder » et « Reprendre » sont le même geste côté serveur : enregistrer.
  // Ils ne diffèrent que par ce que l'utilisateur croit faire.
  const canSave = status === 'UNSAVED' || status === 'TREATED';

  return (
    <li className="row">
      <ScoreRing
        score={business.prospectScore}
        tone={priorityRingTone(business.prospectPriority)}
        label={`Score ${business.prospectScore} sur ${MAX_PROSPECT_SCORE}`}
      />

      <div className="row-body">
        <a
          className="row-name row-name-link"
          href={googleSearchHref(business.name, business.address)}
          target="_blank"
          rel="noopener noreferrer"
          title="Rechercher sur Google"
        >
          {business.name}
        </a>
        <p className="row-meta">
          {business.category !== null ? <span>{business.category}</span> : null}
          <span>{formatDistance(business.distanceMeters)}</span>
        </p>

        <div className="row-facts">
          <span className={`badge ${websiteTone(business.websiteType, business.websiteQuality)}`}>
            {websiteLabel(business.websiteType, business.websiteQuality)}
          </span>

          {inLibrary || status === 'IGNORED' || status === 'TREATED' ? (
            <span className={`badge ${libraryStatusTone(status)}`}>
              {LIBRARY_STATUS_LABELS[status]}
            </span>
          ) : null}

          {business.rating !== null ? (
            <span className="rating">
              <IconStar />
              {formatRating(business.rating)}
              <span className="faint">· {business.userRatingCount ?? 0} avis</span>
            </span>
          ) : null}

          <span className={`hours-${open.state.toLowerCase()}`}>{openStatusLabel(open)}</span>

          {business.phone === null ? (
            <span className="badge badge-danger">Non joignable</span>
          ) : null}
        </div>

        {/* Le pied n'existe que s'il porte quelque chose. */}
        {status === 'UNSAVED' || status === 'IGNORED' || status === 'TREATED' ? (
          <div className="row-foot">
            {status === 'UNSAVED' ? (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => props.onIgnore(business)}
                disabled={pending}
              >
                Ignorer
              </button>
            ) : null}

            {status === 'IGNORED' ? (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => props.onUnignore(business)}
                disabled={pending}
              >
                Ne plus ignorer
              </button>
            ) : null}

            {status === 'TREATED' ? (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => props.onUntreat(business)}
                disabled={pending}
              >
                Oublier
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="row-actions">
        {business.phone !== null ? (
          <a
            className="btn-round btn-round-go"
            href={telHref(business.phone)}
            aria-label={`Appeler ${business.name} au ${business.phone}`}
            title={business.phone}
            onClick={() => props.onCallStart(business)}
          >
            <IconPhone size={16} />
          </a>
        ) : null}

        {canSave ? (
          <button
            type="button"
            className="btn-round btn-round-line"
            onClick={() => props.onSave(business)}
            disabled={pending}
            aria-label={
              status === 'TREATED'
                ? `Reprendre ${business.name} dans la bibliothèque`
                : `Garder ${business.name} dans la bibliothèque`
            }
            title={status === 'TREATED' ? 'Reprendre' : 'Garder'}
          >
            <IconBookmark size={16} />
          </button>
        ) : null}

        {inLibrary ? (
          <span className="btn-round btn-round-on" title="Déjà en bibliothèque">
            <IconBookmark size={16} filled />
          </span>
        ) : null}
      </div>
    </li>
  );
}
